import { useEffect, useState, type FormEvent, type ReactNode } from 'react';
import { Link, NavLink, Outlet, useNavigate } from 'react-router-dom';
import {
  Activity,
  Bell,
  BookOpen,
  Boxes,
  ChartNoAxesCombined,
  ChevronDown,
  ChevronRight,
  CircleHelp,
  FileClock,
  LayoutDashboard,
  Lightbulb,
  GitMerge,
  Menu,
  MessageSquareText,
  PanelLeftClose,
  Plus,
  Road,
  Search,
  Settings,
  ShieldCheck,
  X,
} from 'lucide-react';
import { cx } from '@appcycle/ui';
import { api } from '../lib/api';
import { useApi } from '../lib/hooks';
import { useSession } from '../lib/session';
import { usePortfolio } from '../lib/portfolio';

export function AppMark({ compact = false }: { compact?: boolean }) {
  return <span className={cx('app-mark', compact && 'app-mark--compact')} aria-label="AppCycle">
    <span className="app-mark__loop" aria-hidden="true"><i /><i /><i /><i /></span>
    {!compact && <span>AppCycle</span>}
  </span>;
}

export function Button({ children, variant = 'primary', icon, className, ...props }: React.ButtonHTMLAttributes<HTMLButtonElement> & { variant?: 'primary' | 'secondary' | 'ghost' | 'danger'; icon?: ReactNode }) {
  return <button className={cx('button', `button--${variant}`, className)} {...props}>{icon}{children}</button>;
}

export function StatusLabel({ value, tone }: { value: string; tone?: string }) {
  const normalized = tone ?? value.replaceAll(' ', '_').toLowerCase();
  return <span className={cx('status-label', `status-label--${normalized}`)}><span aria-hidden="true" />{value.replaceAll('_', ' ')}</span>;
}

export function PageHeader({ eyebrow, title, description, action }: { eyebrow?: string; title: string; description?: string; action?: ReactNode }) {
  return <header className="page-header">
    <div>
      {eyebrow && <p className="eyebrow">{eyebrow}</p>}
      <h1>{title}</h1>
      {description && <p>{description}</p>}
    </div>
    {action && <div className="page-header__actions">{action}</div>}
  </header>;
}

export function LoadingState({ label = 'Loading workspace' }: { label?: string }) {
  return <div className="state-card" aria-live="polite"><span className="spinner" aria-hidden="true" /><strong>{label}</strong></div>;
}

export function ErrorState({ message, onRetry }: { message: string; onRetry?: () => void }) {
  return <div className="state-card state-card--error" role="alert"><CircleHelp size={20} /><div><strong>Couldn’t load this view</strong><p>{message}</p></div>{onRetry && <Button variant="secondary" onClick={onRetry}>Try again</Button>}</div>;
}

export function EmptyState({ title, body, action }: { title: string; body: string; action?: ReactNode }) {
  return <div className="empty-state"><span className="empty-state__glyph" aria-hidden="true"><Plus size={20} /></span><h3>{title}</h3><p>{body}</p>{action}</div>;
}

export function Modal({ open, onClose, title, description, children }: { open: boolean; onClose: () => void; title: string; description?: string; children: ReactNode }) {
  useEffect(() => {
    if (!open) return;
    const onKey = (event: KeyboardEvent) => { if (event.key === 'Escape') onClose(); };
    document.addEventListener('keydown', onKey);
    document.body.classList.add('modal-open');
    return () => { document.removeEventListener('keydown', onKey); document.body.classList.remove('modal-open'); };
  }, [onClose, open]);
  if (!open) return null;
  return <div className="modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="modal" role="dialog" aria-modal="true" aria-labelledby="modal-title">
      <header><div><p className="eyebrow">AppCycle</p><h2 id="modal-title">{title}</h2>{description && <p>{description}</p>}</div><button className="icon-button" aria-label="Close dialog" onClick={onClose}><X size={20} /></button></header>
      {children}
    </section>
  </div>;
}

export interface CycleCount { label: string; value: string | number; note: string; tone: 'feedback' | 'roadmap' | 'changelog' | 'decision' }

export function CycleRail({ counts, compact = false }: { counts: CycleCount[]; compact?: boolean }) {
  return <div className={cx('cycle-rail', compact && 'cycle-rail--compact')} aria-label="Product lifecycle">
    {counts.map((item, index) => <div className={cx('cycle-node', `cycle-node--${item.tone}`)} key={item.label}>
      <span className="cycle-node__dot" aria-hidden="true">{index + 1}</span>
      <span><strong>{item.label}</strong><small><b>{item.value}</b> {item.note}</small></span>
      {index < counts.length - 1 && <span className="cycle-node__line" aria-hidden="true" />}
    </div>)}
  </div>;
}

function useWorkspaceTheme() {
  const workspace = useApi<{ accentColor?: string; product?: { accentColor?: string } | null }>('/api/v1/workspace');
  useEffect(() => {
    const accent = workspace.data?.product?.accentColor ?? workspace.data?.accentColor;
    if (accent && /^#[0-9a-f]{6}$/i.test(accent)) document.documentElement.style.setProperty('--cycle-blue', accent);
  }, [workspace.data?.accentColor, workspace.data?.product?.accentColor]);
}

export function PublicShell() {
  useWorkspaceTheme();
  const { user, signIn, isOperator } = useSession();
  const { selectedProduct } = usePortfolio();
  const [navigationOpen, setNavigationOpen] = useState(false);
  return <div className="public-shell">
    <header className="public-header">
      <a href="/" className="public-header__brand"><AppMark />{selectedProduct && <span className="public-product-name">{selectedProduct.name}</span>}</a>
      <nav aria-label="Public navigation">
        <NavLink to="/feedback">Feedback</NavLink>
        <NavLink to="/roadmap">Roadmap</NavLink>
        <NavLink to="/changelog">Changelog</NavLink>
        <NavLink to="/decisions">Decisions</NavLink>
      </nav>
      <div className="public-header__actions">
        {isOperator && <NavLink className="button button--secondary" to="/admin">Open console</NavLink>}
        {!user && <Button onClick={signIn}>Sign in</Button>}
        {user && !isOperator && <span className="user-chip">{user.name}</span>}
      </div>
      <button className="public-header__menu" aria-label={`${navigationOpen ? 'Close' : 'Open'} navigation`} aria-expanded={navigationOpen} aria-controls="public-mobile-navigation" onClick={() => setNavigationOpen((open) => !open)}>{navigationOpen ? <X size={22} /> : <Menu size={22} />}</button>
      <div className={cx('public-mobile-nav', navigationOpen && 'is-open')} id="public-mobile-navigation">
        <nav aria-label="Mobile public navigation">
          <NavLink to="/feedback" onClick={() => setNavigationOpen(false)}>Feedback</NavLink>
          <NavLink to="/roadmap" onClick={() => setNavigationOpen(false)}>Roadmap</NavLink>
          <NavLink to="/changelog" onClick={() => setNavigationOpen(false)}>Changelog</NavLink>
          <NavLink to="/decisions" onClick={() => setNavigationOpen(false)}>Decisions</NavLink>
        </nav>
        {isOperator && <NavLink className="button button--secondary" to="/admin" onClick={() => setNavigationOpen(false)}>Open console</NavLink>}
        {!user && <Button onClick={() => { setNavigationOpen(false); signIn(); }}>Sign in</Button>}
        {user && !isOperator && <span className="user-chip">{user.name}</span>}
      </div>
    </header>
    <main className="public-main"><Outlet /></main>
    <footer className="public-footer"><AppMark /><p>Every change keeps its context.</p><nav><a href="/feedback">Feedback</a><a href="/roadmap">Roadmap</a><a href="/changelog">Changelog</a></nav></footer>
  </div>;
}

const adminNav = [
  { to: '/admin', label: 'Overview', icon: LayoutDashboard, end: true },
  { to: '/admin/feedback', label: 'Feedback', icon: MessageSquareText },
  { to: '/admin/roadmap', label: 'Roadmap', icon: Road },
  { to: '/admin/timeline', label: 'Timeline', icon: GitMerge },
  { to: '/admin/changelog', label: 'Changelog', icon: FileClock },
  { to: '/admin/decisions', label: 'Decisions', icon: Lightbulb },
  { to: '/admin/analytics', label: 'Analytics', icon: ChartNoAxesCombined },
  { to: '/admin/portfolio', label: 'Products', icon: Boxes },
  { to: '/admin/settings', label: 'Settings', icon: Settings },
];

export function AdminShell() {
  useWorkspaceTheme();
  const { user, loading, isOperator, signIn, signOut } = useSession();
  const [navigationOpen, setNavigationOpen] = useState(false);
  if (loading) return <LoadingState />;
  if (!user) return <div className="access-gate"><AppMark /><ShieldCheck size={34} /><h1>Operator sign-in required</h1><p>Use your Authrz.me account to open the AppCycle console.</p><Button onClick={signIn}>Continue with Authrz.me</Button></div>;
  if (!isOperator) return <div className="access-gate"><AppMark /><ShieldCheck size={34} /><h1>Console access isn’t assigned</h1><p>Your AppCycle role allows public participation but not product administration.</p><a className="button button--secondary" href="/feedback">Return to feedback</a></div>;
  return <div className="admin-shell">
    <aside className="admin-sidebar">
      <a href="/admin"><AppMark /></a>
      <nav aria-label="Console navigation">
        {adminNav.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} className={({ isActive }) => cx(isActive && 'active')}><Icon size={17} /><span>{label}</span></NavLink>)}
      </nav>
      <div className="admin-sidebar__bottom"><a href="/feedback"><PanelLeftClose size={17} />Public portal</a><button onClick={() => void signOut()}><span className="avatar">{user.name.slice(0, 2).toUpperCase()}</span><span><strong>{user.name}</strong><small>Sign out</small></span></button></div>
    </aside>
    <header className="admin-topbar"><button className="icon-button admin-topbar__menu" aria-label={`${navigationOpen ? 'Close' : 'Open'} console navigation`} aria-expanded={navigationOpen} aria-controls="admin-mobile-drawer" onClick={() => setNavigationOpen((open) => !open)}>{navigationOpen ? <X size={20} /> : <Menu size={20} />}</button><ProductContextSwitcher /><AdminGlobalSearch /><AdminNotificationMenu /></header>
    <main className="admin-main"><Outlet /></main>
    <nav className="mobile-admin-nav" aria-label="Mobile console navigation">{adminNav.slice(1, 5).map(({ to, label, icon: Icon }) => <NavLink key={to} to={to}><Icon size={19} /><span>{label}</span></NavLink>)}</nav>
    {navigationOpen && <><button className="admin-mobile-scrim is-open" aria-label="Close console navigation" onClick={() => setNavigationOpen(false)} />
    <aside className="admin-mobile-drawer is-open" id="admin-mobile-drawer" aria-label="Console menu">
      <header><AppMark /><button className="icon-button" aria-label="Close console navigation" onClick={() => setNavigationOpen(false)}><X size={20} /></button></header>
      <ProductContextSwitcher mobile />
      <nav>{adminNav.map(({ to, label, icon: Icon, end }) => <NavLink key={to} to={to} end={end} onClick={() => setNavigationOpen(false)}><Icon size={18} /><span>{label}</span></NavLink>)}</nav>
      <div className="admin-mobile-drawer__bottom"><a href="/feedback"><PanelLeftClose size={18} />Public portal</a><button onClick={() => void signOut()}><span className="avatar">{user.name.slice(0, 2).toUpperCase()}</span><span><strong>{user.name}</strong><small>Sign out</small></span></button></div>
    </aside></>}
  </div>;
}

function ProductContextSwitcher({ mobile = false }: { mobile?: boolean }) {
  const { portfolio, selectedProduct, loading, selectProduct } = usePortfolio();
  const [open, setOpen] = useState(false);
  const team = portfolio?.teams.find((item) => item.id === selectedProduct?.teamId);
  const organization = portfolio?.organizations.find((item) => item.id === selectedProduct?.organizationId);
  const alternatives = portfolio?.products.filter((item) => item.id !== selectedProduct?.id) ?? [];

  if (loading || !selectedProduct) {
    return <div className={cx('product-context-switcher', mobile && 'product-context-switcher--mobile', 'is-loading')}><span /><span /></div>;
  }

  return <div className={cx('product-context-switcher', mobile && 'product-context-switcher--mobile')}>
    <button type="button" aria-expanded={open} aria-controls={mobile ? 'mobile-product-menu' : 'desktop-product-menu'} onClick={() => setOpen((value) => !value)}>
      <span className="product-context-switcher__mark" style={{ background: selectedProduct.accentColor }}>{selectedProduct.name.slice(0, 1).toUpperCase()}</span>
      <span><small>{organization?.name ?? 'Organization'} / {team?.name ?? 'Team'}</small><strong>{selectedProduct.name}</strong></span>
      <ChevronDown size={15} />
    </button>
    {open && <section className="product-context-menu" id={mobile ? 'mobile-product-menu' : 'desktop-product-menu'}>
      <header><span><small>Current product</small><strong>{selectedProduct.name}</strong></span><Link to="/admin/portfolio" onClick={() => setOpen(false)}>Manage</Link></header>
      {alternatives.length ? <div>{alternatives.map((product) => {
        const optionTeam = portfolio?.teams.find((item) => item.id === product.teamId);
        const optionOrganization = portfolio?.organizations.find((item) => item.id === product.organizationId);
        return <button type="button" key={product.id} onClick={() => { selectProduct(product.id); setOpen(false); }}>
          <span className="product-context-switcher__mark" style={{ background: product.accentColor }}>{product.name.slice(0, 1).toUpperCase()}</span>
          <span><small>{optionOrganization?.name} / {optionTeam?.name}</small><strong>{product.name}</strong></span>
          <ChevronRight size={15} />
        </button>;
      })}</div> : <p>No other products are available.</p>}
    </section>}
  </div>;
}

interface SearchResult { id: string; kind: 'post' | 'roadmap' | 'changelog' | 'decision'; title: string; excerpt?: string }

function AdminGlobalSearch() {
  const navigate = useNavigate();
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [open, setOpen] = useState(false);
  useEffect(() => {
    if (query.trim().length < 2) { setResults([]); setOpen(false); return; }
    const timer = window.setTimeout(() => {
      void api<{ items: SearchResult[] }>(`/api/v1/search?q=${encodeURIComponent(query.trim())}`)
        .then((response) => { setResults(response.items); setOpen(true); })
        .catch(() => { setResults([]); setOpen(true); });
    }, 180);
    return () => window.clearTimeout(timer);
  }, [query]);
  const resultUrl = (result: SearchResult) => result.kind === 'post' ? `/admin/feedback?selected=${result.id}` : `/admin/${result.kind === 'roadmap' ? 'roadmap' : result.kind === 'decision' ? 'decisions' : 'changelog'}?selected=${result.id}`;
  function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (results[0]) { setOpen(false); navigate(resultUrl(results[0])); }
  }
  return <form className="global-search-wrap" role="search" onSubmit={submit} onBlur={(event) => { if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setOpen(false); }}>
    <label className="global-search"><Search size={16} /><span className="sr-only">Search product data</span><input value={query} onChange={(event) => setQuery(event.target.value)} onFocus={() => query.trim().length >= 2 && setOpen(true)} placeholder="Search product data…" autoComplete="off" /></label>
    {open && <div className="global-search-results" role="listbox" aria-label="Product search results">
      {results.length ? results.map((result) => <Link className={`relationship-step--${result.kind}`} key={`${result.kind}-${result.id}`} to={resultUrl(result)} role="option" onClick={() => { setOpen(false); setQuery(''); }}><span className="relationship-step__dot" /><span><small>{result.kind}</small><strong>{result.title}</strong></span><ChevronRight size={15} /></Link>) : <p>No matching product records.</p>}
    </div>}
  </form>;
}

interface NotificationRow { id: string; event_type: string; title: string; state: string; created_at: string }

function AdminNotificationMenu() {
  const [open, setOpen] = useState(false);
  const outbox = useApi<{ items: NotificationRow[] }>('/api/v1/notifications/outbox?pageSize=6');
  const pending = outbox.data?.items.filter((item) => item.state === 'pending' || item.state === 'retry').length ?? 0;
  return <div className="notification-menu">
    <button className="icon-button" aria-label="Notifications" aria-expanded={open} aria-controls="notification-popover" onClick={() => setOpen((value) => !value)}><Bell size={18} />{pending > 0 && <span className="notification-menu__dot" aria-label={`${pending} pending`} />}</button>
    {open && <section className="notification-popover" id="notification-popover"><header><div><p className="eyebrow">Delivery outbox</p><h2>Notifications</h2></div><button className="text-button" onClick={() => void outbox.refresh()}>Refresh</button></header>{outbox.loading ? <LoadingState label="Loading notifications" /> : outbox.data?.items.length ? <div>{outbox.data.items.map((item) => <article key={item.id}><span><small>{item.event_type}</small><strong>{item.title}</strong><time>{new Date(item.created_at).toLocaleString()}</time></span><StatusLabel value={item.state} /></article>)}</div> : <EmptyState title="No delivery events" body="TopicBell delivery activity will appear here." />}</section>}
  </div>;
}

export function RelationshipTrail({ items }: { items: Array<{ kind: string; title: string; verb?: string }> }) {
  return <div className="relationship-trail">{items.map((item, index) => <div className={cx('relationship-step', `relationship-step--${item.kind}`)} key={`${item.kind}-${index}`}><span className="relationship-step__dot" /><div><small>{item.kind}</small><strong>{item.title}</strong>{item.verb && <em>{item.verb}</em>}</div>{index < items.length - 1 && <ChevronRight size={16} aria-hidden="true" />}</div>)}</div>;
}

export const artifactIcons = { post: MessageSquareText, roadmap: Boxes, changelog: BookOpen, decision: Lightbulb, activity: Activity };
